import {
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PencilIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { TicketPlusIcon, TicketIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'react-hot-toast'; // Import Toaster component from react-hot-toast
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/useAuth';
import { useDepartments } from '../../../contexts/DepartmentContext';
import { CreateUserData, userService } from '../../../services/userService';
import { User } from '../../../types/user';
import Pagination from '../../common/Pagination';
import { Dialog, DialogContent, DialogTitle } from '../../ui/custom/Dialog';

interface AgentManagerProps {
  title: string;
  description: string;
}

export default function AgentManager({ title, description }: AgentManagerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    full_name: '',
    phone: '',
    email: '',
    role: 'agent',
    department_id: undefined
  });
  const [error, setError] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const { theme } = useTheme();
  const { departments } = useDepartments();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(() => {
    const saved = localStorage.getItem('agentManagerPageLimit');
    return saved ? parseInt(saved, 10) : 10;
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await userService.getUsers();
      // Filter to only show agents and admins, not customers
      setUsers(
        response.filter(
          (user) => user.role === 'agent' || user.role === 'admin' || user.role === 'requester'
        )
      );
    } catch (error) {
      setError(t('settings.userManagement.errors.fetchFailed'));
      console.error('Error fetching users:', error);
    }
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    localStorage.setItem('agentManagerPageLimit', newLimit.toString());
  };

  const startIndex = (page - 1) * limit;
  const paginatedUsers = users.slice(startIndex, startIndex + limit);
  const totalPages = Math.ceil(users.length / limit);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, {
          full_name: formData.full_name,
          phone: formData.phone,
          email: formData.email,
          role: formData.role as 'admin' | 'agent' | 'requester',
          department_id: formData.department_id
        });
      } else {
        await userService.createUser({
          full_name: formData.full_name,
          phone: formData.phone,
          email: formData.email,
          role: formData.role as 'admin' | 'agent' | 'requester',
          department_id: formData.department_id
        });
      }

      setIsModalOpen(false);
      setEditingUser(null);
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        role: 'agent',
        department_id: undefined
      });
      fetchUsers();
      toast.success(t('settings.userManagement.success.userSaved')); // Use toast.success for notifications
    } catch (error) {
      setError(
        error instanceof Error ? error.message : t('settings.userManagement.errors.saveFailed')
      );
      console.error('Error saving user:', error);
      toast.error(
        error instanceof Error ? error.message : t('settings.userManagement.errors.saveFailed')
      );
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await userService.deleteUser(userToDelete.id);
      toast.success(response.message); // Use toast.success for notifications
      fetchUsers();
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message); // Use toast.error for notifications
      } else if (typeof error === 'object' && error && 'message' in error) {
        toast.error(error.message as string); // Use toast.error for notifications
      } else {
        toast.error(t('settings.userManagement.errors.deleteFailed')); // Use toast.error for notifications
      }
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDelete = async (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name ?? 'error_fetching_name',
      phone: user.phone ?? 'error_fetching_phone',
      email: user.email,
      role: user.role || 'agent',
      department_id: user.department_id
    });
    setIsModalOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setUserToReset(user);
    setIsResetModalOpen(true);
  };

  const handleViewTickets = (user: User) => {
    navigate(`/agent-tickets/${user.id}`);
  };

  const confirmResetPassword = async () => {
    if (!userToReset) return;

    try {
      const response = await userService.resetPassword(userToReset.id);
      setTempPassword(response.tempPassword);
      // Don't close the modal yet, as we need to show the temporary password
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to reset password');
      console.error('Error resetting password:', error);
      setIsResetModalOpen(false);
      setUserToReset(null);
      toast.error(error instanceof Error ? error.message : 'Failed to reset password'); // Use toast.error for notifications
    }
  };

  const handleCopyPassword = async () => {
    if (tempPassword) {
      try {
        await navigator.clipboard.writeText(tempPassword);
        // Optionally show a success message
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleCloseResetModal = () => {
    setIsResetModalOpen(false);
    setUserToReset(null);
    setTempPassword(null);
  };

  return (
    <>
      <Toaster position="top-right" /> {/* Add Toaster component to the JSX */}
      <div className="space-y-6">
        {error && (
          <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-red-200' : 'text-red-700'}`}>
              {error}
            </p>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <div>
            <h2
              className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
            >
              {title}
            </h2>
            {description && (
              <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {description}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              setEditingUser(null);
              setFormData({
                full_name: '',
                phone: '',
                email: '',
                role: 'agent',
                department_id: undefined
              });
              setIsModalOpen(true);
            }}
            className="mr-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('settings.userManagement.addNew')}
          </button>
        </div>

        {/* Total Agents Stats Bar */}
        <div
          className={`rounded-lg shadow-sm p-3 border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} grid grid-cols-1 md:grid-cols-4 gap-4 my-6`}
        >
          <div className={`rounded-lg`}>
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'} rounded-md p-3`}
              >
                <UserGroupIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-indigo-200' : 'text-indigo-800'}`}
                />
              </div>
              <div className="ml-4">
                <p
                  className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  {t('settings.userManagement.totalAgents')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {users.length}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-lg`}>
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 ${theme === 'dark' ? 'bg-purple-900/50' : 'bg-purple-100'} rounded-md p-3`}
              >
                <ShieldCheckIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-purple-200' : 'text-purple-800'}`}
                />
              </div>
              <div className="ml-4">
                <p
                  className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  {t('settings.userManagement.roles.admin')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {users.filter((user) => user.role === 'admin').length}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-lg`}>
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'} rounded-md p-3`}
              >
                <TicketPlusIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}
                />
              </div>
              <div className="ml-4">
                <p
                  className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  {t('settings.userManagement.roles.requester')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {users.filter((user) => user.role === 'requester').length}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-lg`}>
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 ${theme === 'dark' ? 'bg-green-900/50' : 'bg-green-100'} rounded-md p-3`}
              >
                <UserIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-green-200' : 'text-green-800'}`}
                />
              </div>
              <div className="ml-4">
                <p
                  className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  {t('settings.userManagement.roles.agent')}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {users.filter((user) => user.role === 'agent').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="overflow-x-auto">
            <table
              className={`min-w-full divide-y ${
                theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
              }`}
            >
              <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.userManagement.columns.name')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.userManagement.columns.phone')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.userManagement.columns.email')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.userManagement.columns.role')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {t('settings.userManagement.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody
                className={
                  theme === 'dark'
                    ? 'bg-gray-800 divide-y divide-gray-700'
                    : 'bg-white divide-y divide-gray-200'
                }
              >
                {paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                      }`}
                    >
                      <div className="flex items-center">
                        <span>{user.full_name}</span>
                        {currentUser?.id === user.id && (
                          <span
                            className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              theme === 'dark'
                                ? 'bg-indigo-900/50 text-indigo-200'
                                : 'bg-indigo-100 text-indigo-800'
                            }`}
                          >
                            {t('settings.userManagement.me')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}
                    >
                      {user.phone}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}
                    >
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          theme === 'dark'
                            ? user.role === 'admin'
                              ? 'bg-purple-900/50 text-purple-200'
                              : user.role === 'requester'
                                ? 'bg-blue-900/50 text-blue-200'
                                : 'bg-green-900/50 text-green-200'
                            : user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : user.role === 'requester'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewTickets(user)}
                        className={
                          theme === 'dark'
                            ? 'text-green-400 hover:text-green-300'
                            : 'text-green-600 hover:text-green-900'
                        }
                        title="View tickets"
                      >
                        <TicketIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className={
                          theme === 'dark'
                            ? 'text-indigo-400 hover:text-indigo-300'
                            : 'text-indigo-600 hover:text-indigo-900'
                        }
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(user)}
                        className={
                          theme === 'dark'
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-900'
                        }
                        title="Reset password"
                      >
                        <KeyIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className={
                          currentUser?.id === user.id
                            ? theme === 'dark'
                              ? 'text-gray-600 cursor-not-allowed'
                              : 'text-gray-300 cursor-not-allowed'
                            : theme === 'dark'
                              ? 'text-red-400 hover:text-red-300'
                              : 'text-red-600 hover:text-red-900'
                        }
                        disabled={currentUser?.id === user.id}
                        title={
                          currentUser?.id === user.id
                            ? t('settings.userManagement.cannotDeleteOwnAccount')
                            : 'Delete user'
                        }
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div
                className={`px-6 py-4 flex items-center justify-center border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  itemsPerPage={limit}
                  itemsPerPageOptions={[5, 10, 20, 50]}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
          <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  theme === 'dark' ? 'bg-red-900/30' : 'bg-red-100'
                }`}
              >
                <ExclamationTriangleIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  className={`text-base font-semibold leading-6 ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {t('settings.userManagement.deleteAgent')}
                </DialogTitle>
                <div className="mt-2">
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('settings.userManagement.confirmDelete')} '{userToDelete?.full_name}'? This
                    agent will also be removed from all assigned support groups. This action cannot
                    be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 sm:ml-3 sm:w-auto sm:text-sm ${
                  theme === 'dark'
                    ? 'bg-red-600 hover:bg-red-500 focus:ring-offset-gray-800 focus:ring-red-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-offset-2 focus:ring-red-500'
                }`}
                onClick={handleDeleteUser}
              >
                {t('metadata.delete')}
              </button>
              <button
                type="button"
                className={`mt-3 inline-flex w-full justify-center rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 sm:mt-0 sm:w-auto sm:text-sm ${
                  theme === 'dark'
                    ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Confirmation Modal */}
        <Dialog open={isResetModalOpen} onOpenChange={setIsResetModalOpen}>
          <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  theme === 'dark' ? 'bg-indigo-900/30' : 'bg-indigo-100'
                }`}
              >
                <KeyIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  className={`text-base font-semibold leading-6 ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {tempPassword
                    ? t('settings.userManagement.tempPasswordGenerated')
                    : t('settings.userManagement.resetPasswordTitle')}
                </DialogTitle>
                {!tempPassword ? (
                  <div className="mt-2">
                    <p
                      className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                    >
                      {t('settings.userManagement.confirmResetPassword')} {userToReset?.full_name}?
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p
                      className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}
                    >
                      {t('settings.userManagement.tempPasswordMessage')} {userToReset?.full_name}.
                      Make sure to copy it before closing this dialog.
                    </p>
                    <div
                      className={`mt-3 flex items-center space-x-2 p-3 rounded-md ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                      }`}
                    >
                      <code
                        className={`flex-1 font-mono text-sm ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                        }`}
                      >
                        {tempPassword}
                      </code>
                      <button
                        onClick={handleCopyPassword}
                        className={`p-2 focus:outline-none ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-gray-300'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title={t('settings.userManagement.copyToClipboard')}
                      >
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              {!tempPassword ? (
                <>
                  <button
                    type="button"
                    onClick={confirmResetPassword}
                    className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm ${
                      theme === 'dark'
                        ? 'bg-indigo-600 hover:bg-indigo-500 focus:ring-offset-gray-800 focus:ring-indigo-500'
                        : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-2 focus:ring-indigo-500'
                    }`}
                  >
                    {t('settings.userManagement.resetPassword')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseResetModal}
                    className={`mt-3 inline-flex w-full justify-center rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-offset-2 focus:ring-indigo-500'
                    }`}
                  >
                    {t('common.cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleCloseResetModal}
                  className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm ${
                    theme === 'dark'
                      ? 'bg-indigo-600 hover:bg-indigo-500 focus:ring-offset-gray-800 focus:ring-indigo-500'
                      : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                >
                  Close
                </button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  theme === 'dark' ? 'bg-indigo-900/30' : 'bg-indigo-100'
                }`}
              >
                <UserPlusIcon
                  className={`h-6 w-6 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`}
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <DialogTitle
                  className={`text-base font-semibold leading-6 ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {editingUser
                    ? t('settings.userManagement.editAgent')
                    : t('settings.userManagement.addAgent')}
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                  <div className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="full_name"
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {t('settings.userManagement.fullName')}
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          id="full_name"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                          required
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="phone"
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {t('settings.userManagement.phone')}
                        </label>
                        <input
                          type="text"
                          name="phone"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                          required
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {t('settings.userManagement.email')}
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                          required
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="role"
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {t('settings.userManagement.role')}
                        </label>
                        <select
                          name="role"
                          id="role"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                        >
                          <option value="agent">{t('settings.userManagement.roles.agent')}</option>
                          <option value="admin">{t('settings.userManagement.roles.admin')}</option>
                          <option value="requester">
                            {t('settings.userManagement.roles.requester')}
                          </option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="department"
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          Department
                        </label>
                        <select
                          name="department"
                          id="department"
                          value={formData.department_id || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              department_id: e.target.value ? Number(e.target.value) : undefined
                            })
                          }
                          className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 sm:text-sm ${
                            theme === 'dark'
                              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                        >
                          <option value="">Select Department</option>
                          {departments.map((dept) => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className={`inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 sm:ml-3 sm:w-auto sm:text-sm ${
                        theme === 'dark'
                          ? 'bg-indigo-600 hover:bg-indigo-500 focus:ring-offset-gray-800 focus:ring-indigo-500'
                          : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-2 focus:ring-indigo-500'
                      }`}
                    >
                      {editingUser
                        ? t('settings.userManagement.saveChanges')
                        : t('settings.userManagement.create')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className={`mt-3 inline-flex w-full justify-center rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 sm:mt-0 sm:w-auto sm:text-sm ${
                        theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-offset-gray-800 focus:ring-indigo-500'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-offset-2 focus:ring-indigo-500'
                      }`}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
